import { useState } from 'react';
import { X, Globe, Clock, Activity } from 'lucide-react';
import dayjs from 'lib/dayjs';
import { useGetUserSessionsQuery, useGetMySessionsQuery } from '../redux/features/users/userApi';
import Pagination from './ui/Pagination';

const UserSessionsModal = ({ isOpen, onClose, user, isSelf = false }) => {
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const mySessions = useGetMySessionsQuery({ pageNo, pageSize }, { skip: !isOpen || !isSelf });
  const userSessions = useGetUserSessionsQuery(
    { id: user?.id, pageNo, pageSize },
    { skip: !isOpen || isSelf || !user?.id },
  );

  const sessionsDataResult = isSelf ? mySessions?.data : userSessions?.data;
  const isLoading = isSelf ? mySessions.isFetching : userSessions.isFetching;
  const isError = isSelf ? mySessions.isError : userSessions.isError;

  if (!isOpen) return null;

  const sessionsData = sessionsDataResult?.data;
  const logs = sessionsData?.logs || [];
  const totalCount = sessionsData?.total || 0;

  const getUserAgent = (agent = '') => {
    let os = 'Unknown';
    let browser = 'Unknown';

    // OS detection
    if (/Windows/i.test(agent)) os = 'Windows';
    else if (/Mac OS|Macintosh/i.test(agent)) os = 'Mac';
    else if (/Linux/i.test(agent)) os = 'Linux';
    else if (/Android/i.test(agent)) os = 'Android';
    else if (/iPhone|iPad/i.test(agent)) os = 'iOS';

    // Browser detection
    if (/Chrome/i.test(agent) && !/Edg|OPR/i.test(agent)) browser = 'Chrome';
    else if (/Edg/i.test(agent)) browser = 'Edge';
    else if (/Firefox/i.test(agent)) browser = 'Firefox';
    else if (/Safari/i.test(agent) && !/Chrome/i.test(agent)) browser = 'Safari';
    else if (/OPR/i.test(agent)) browser = 'Opera';

    return `${os} / ${browser}`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-5xl bg-white rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header (Fixed) */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Login History</h3>
                {!isSelf && (
                  <p className="text-sm text-gray-500">
                    {user?.first_name} {user?.last_name} ({user?.employee_id})
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Body (Scrollable if content exceeds max-height) */}
          <div className="p-6 flex flex-col flex-1 min-h-[400px] overflow-hidden">
            <div className="space-y-4 flex flex-col flex-1 overflow-hidden">
              <div className="overflow-auto rounded-xl border border-gray-200 flex-1 relative max-h-[380px]">
                {/* Fixed Header Table with Scrollable Body */}
                <table className="w-full text-sm text-left table-fixed border-collapse">
                  <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200 sticky top-0 z-20">
                    <tr>
                      <th className="px-4 py-3.5 w-40 bg-gray-50">IP Address</th>
                      <th className="px-4 py-3.5 w-40 bg-gray-50">OS / Browser</th>
                      <th className="px-4 py-3.5 w-40 bg-gray-50">Start</th>
                      <th className="px-4 py-3.5 w-40 bg-gray-50">End</th>
                      <th className="px-4 py-3.5 w-40 text-center bg-gray-50">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 relative">
                    {isError ? (
                      <tr>
                        <td colSpan="5" className="py-20">
                          <div className="bg-red-50 text-red-700 p-6 rounded-xl text-center mx-10">
                            <p className="font-semibold">Error</p>
                            <p className="text-sm">Failed to load session history.</p>
                          </div>
                        </td>
                      </tr>
                    ) : logs.length === 0 && !isLoading ? (
                      <tr>
                        <td colSpan="5" className="py-20">
                          <div className="text-center py-20">
                            <div className="mx-auto w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                              <Activity className="h-8 w-8 text-gray-300" />
                            </div>
                            <p className="text-gray-500 text-lg">No session history found</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <>
                        {logs.map((session, index) => (
                          <tr
                            key={session.id || index}
                            className={`transition-colors hover:bg-blue-50/30 h-[57px] ${index % 2 === 0 ? 'bg-gray-100' : 'bg-white'}`}
                          >
                            <td className="px-4 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <Globe className="h-4 w-4 text-gray-400" />
                                <span className="font-mono text-gray-700">
                                  {session.ip_address || 'N/A'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="max-w-[200px] truncate group relative">
                                <span className="text-gray-600" title={session.user_agent}>
                                  {getUserAgent(session.user_agent) || 'Unknown'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-gray-600">
                              <div className="flex flex-col">
                                <span>{dayjs(session.created_at).format('MMM DD, YYYY')}</span>
                                <span className="text-[10px] text-gray-400 font-mono">
                                  {dayjs(session.created_at).format('hh:mm:ss A')}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-gray-600">
                              {session?.is_active ? (
                                <span className="text-green-600 font-medium">Logged In</span>
                              ) : (
                                <div className="flex flex-col">
                                  <span>{dayjs(session.last_activity).format('MMM DD, YYYY')}</span>
                                  <span className="text-[10px] text-gray-400 font-mono">
                                    {dayjs(session.last_activity).format('hh:mm:ss A')}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-center">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                                  session.is_active
                                    ? 'bg-green-100 text-green-700 animate-pulse'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {session.is_active ? 'Active' : 'Closed'}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {/* Always render empty rows to fill pageSize and maintain height */}
                        {logs.length < pageSize &&
                          [...Array(pageSize - logs.length)].map((_, i) => (
                            <tr
                              key={`empty-${i}`}
                              className={`h-[57px] ${(logs.length + i) % 2 === 0 ? 'bg-gray-100' : 'bg-white'}`}
                            >
                              <td colSpan="5"></td>
                            </tr>
                          ))}
                      </>
                    )}

                    {/* Persistent Loading Overlay to prevent jump */}
                    {isLoading && (
                      <div className="absolute inset-0 z-30 bg-white/40 backdrop-blur-[1px] flex flex-col items-center justify-center space-y-3">
                        <div className="h-10 w-10 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin shadow-sm" />
                        <p className="text-blue-700 font-semibold text-sm bg-white/80 px-4 py-1.5 rounded-full shadow-sm border border-blue-50">
                          Updating results...
                        </p>
                      </div>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex-shrink-0 pt-2 border-t border-gray-50">
                <Pagination
                  currentPage={pageNo}
                  totalItems={totalCount}
                  pageSize={pageSize}
                  onPageChange={setPageNo}
                  onPageSizeChange={setPageSize}
                />
              </div>
            </div>
          </div>

          {/* Footer (Fixed) */}
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end flex-shrink-0">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-white border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-sm active:scale-95"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSessionsModal;
